// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * What a scanned QR code turned out to be.
 *
 * `friendInvite` exists so the start screen can say "sign in first" instead of the useless
 * "not a Goffy code" — a friend invite is a real Goffy code, it just cannot be redeemed by
 * someone who is not signed in yet.
 */
export type InviteQrScan =
  | { kind: 'invite'; inviteToken: string }
  | { kind: 'friendInvite' }
  | { kind: 'unknown' };

/** `https://goffy.app/tour/invite/<token>` — the link behind every tour and round share QR. */
const TOUR_INVITE_PATH = /\/tour\/invite\/([^/?#\s]+)/i;
/** The viewer links append the token as a query param instead of a path segment. */
const INVITE_TOKEN_QUERY = /[?&]inviteToken=([^&#\s]+)/i;
const FRIEND_INVITE_PATH = /\/friend\/invite(?:[/?#]|$)/i;
const LOOKS_LIKE_URL = /^[a-z][a-z0-9+.-]*:\/\//i;

const decode = (value: string) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Turns whatever the camera read into something the start screen can act on.
 *
 * Scans arrive in three shapes: a full share link (the QR codes Goffy itself prints), a bare
 * join code (someone generated a code-only QR, or the text was typed into one), and everything
 * else the camera happens to see. A share-link token is passed through untouched — unlike a
 * typed join code it is not guaranteed to be alphanumeric, so stripping it would corrupt it.
 */
export const parseInviteQrPayload = (raw: string): InviteQrScan => {
  const value = String(raw ?? '').trim();
  if (!value) {
    return { kind: 'unknown' };
  }

  const isUrl = LOOKS_LIKE_URL.test(value) || value.startsWith('/');
  if (isUrl) {
    const path = value.match(TOUR_INVITE_PATH);
    if (path?.[1]) {
      return { kind: 'invite', inviteToken: decode(path[1]).trim() };
    }

    const query = value.match(INVITE_TOKEN_QUERY);
    if (query?.[1]) {
      return { kind: 'invite', inviteToken: decode(query[1]).trim() };
    }

    if (FRIEND_INVITE_PATH.test(value)) {
      return { kind: 'friendInvite' };
    }

    return { kind: 'unknown' };
  }

  // Same normalisation the typed-code field applies, so a scanned "ABC-123" and a typed
  // "abc123" reach the BFF as the same token.
  const code = value.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  return code ? { kind: 'invite', inviteToken: code } : { kind: 'unknown' };
};
